import React, { useMemo } from "react";
import { useAppState } from "../../../context/StateContext";
import { Grid } from "@mui/material";
import { TimelineSeparator } from "@mui/lab";
import { colors } from "@zapalacorp/tokens";
import { parse, isValid, format } from "date-fns";
import { Item } from ".";
import IconCalendar from "../Icons/IconCalender";
import IconUser from "../Icons/IconUser";
import { TbWorld } from "react-icons/tb";
import {
  ActivityConnector,
  ActivityContent,
  ActivityDot,
  ActivityDotCenter,
  ActivityInfo,
  ActivityInfoContent,
  ActivityItem,
  ActivityOppositeContent,
  ActivitySubTitle,
  ActivityTitle,
} from "./styles";

interface HistoryItemProps {
  renderIcon?: (item: Item) => JSX.Element;
  isLastItem: boolean;
  item: Item;
}

const HistoryItem = ({ renderIcon, isLastItem, item }: HistoryItemProps) => {
  const { currentUser } = useAppState();

  const formattedDate = useMemo(() => {
    if (!item.activityDate) return "";

    const userDatePreference = currentUser?.preferences?.dateFormat;
    const userTimePreference = currentUser?.preferences?.timeFormat;

    const preferredDateFormat = userDatePreference === "MM_DD_YYYY" ? "MM/dd/yyyy" : "dd/MM/yyyy";
    const preferredTimeFormat = userTimePreference === "12_HOUR" ? "hh:mm a" : "HH:mm";
    const dateTimeFormated = `${preferredDateFormat} ${preferredTimeFormat}`;

    const incomingFormat = "dd/MM/yyyy hh:mmaaa";

    const date = parse(item.activityDate, incomingFormat, new Date());

    if (isValid(date)) {
      return format(date, dateTimeFormated);
    }

    return item.activityDate;
  }, [item.activityDate, currentUser?.preferences]);

  return (
    <ActivityItem>
      <ActivityOppositeContent />
      <TimelineSeparator>
        {renderIcon ? (
          renderIcon(item)
        ) : (
          <ActivityDot>
            <ActivityDotCenter />
          </ActivityDot>
        )}
        {!isLastItem && <ActivityConnector />}
      </TimelineSeparator>
      <ActivityContent>
        <Grid container direction="column" alignItems="flex-start">
          <ActivityTitle>{item.title}</ActivityTitle>
          <ActivityInfo container alignItems="flex-start" direction="row">
            <ActivityInfoContent>
              <IconCalendar width={12} height={12} color={colors.gray600} />
              <ActivitySubTitle>{formattedDate}</ActivitySubTitle>
            </ActivityInfoContent>
            {item?.activityUser && (
              <ActivityInfoContent>
                <IconUser width={12} height={12} color={colors.gray600} />
                <ActivitySubTitle>{item?.activityUser}</ActivitySubTitle>
              </ActivityInfoContent>
            )}
            {item?.activityUserIp && (
              <ActivityInfoContent>
                <TbWorld width={12} height={12} color={colors.gray600} />
                <ActivitySubTitle>{item?.activityUserIp}</ActivitySubTitle>
              </ActivityInfoContent>
            )}
          </ActivityInfo>
        </Grid>
      </ActivityContent>
    </ActivityItem>
  );
};

const areEqual = (prevProps: HistoryItemProps, nextProps: HistoryItemProps) =>
  JSON.stringify(prevProps) === JSON.stringify(nextProps);

export default React.memo(HistoryItem, areEqual);
